import React, { useEffect, useMemo, useState } from "react";
import { Button, SelectMenu, Text } from "@zorni/components-lib";
import { useTranslation } from "react-i18next";

import { USER_ROLE } from "../../../../common/model/enum";
import { useAdmin } from "../../../../hooks/useAdmin";
import { useUserFormValidation } from "../hooks/useUserFormValidation";
import styles from "./PreviewUser.module.css";

const ADMIN_ROLE_KEYS = new Set([USER_ROLE.PA, "PRO_ADMIN", "PRO-ADMIN"]);

const isProAdminRole = (role) => ADMIN_ROLE_KEYS.has(role);

const PreviewUser = ({
  user,
  roles,
  draftRole,
  roleOptions,
  establishmentOptions,
  onRoleUpdate,
  onRoleRemove,
  onDraftRoleChange,
  onDraftRoleAdd,
}) => {
  const { t } = useTranslation();
  const { hasPARole, hasPaRole, isAuthUserOwner, isProAdminForEstablishment } =
    useAdmin();
  const {
    errors: draftRoleErrors,
    validateForm: validateDraftRoleForm,
    clearErrors: clearDraftRoleErrors,
  } = useUserFormValidation(draftRole, { requireEmail: false });
  const [isAddFormVisible, setIsAddFormVisible] = useState(false);

  const canAccessPage = Boolean(hasPaRole ?? hasPARole);

  const fullName = useMemo(() => {
    const firstName = user?.firstName || "";
    const lastName = user?.lastName || "";
    return `${firstName} ${lastName}`.trim();
  }, [user?.firstName, user?.lastName]);

  const userInitial = useMemo(() => {
    if (fullName) return fullName.charAt(0).toUpperCase();
    if (user?.email) return user.email.charAt(0).toUpperCase();
    return "-";
  }, [fullName, user?.email]);

  useEffect(() => {
    if (!draftRole?.establishment && !draftRole?.role) {
      setIsAddFormVisible(false);
    }
  }, [draftRole?.establishment, draftRole?.role]);

  if (!canAccessPage) {
    return null;
  }

  const getUserPermissions = (establishmentId, targetRole) => {
    const canManageUser = isProAdminForEstablishment(establishmentId);
    const isTargetProAdmin = isProAdminRole(targetRole);

    const canEditRole =
      canManageUser && (!isTargetProAdmin || isAuthUserOwner === true);

    const canDeleteUser =
      canManageUser && (!isTargetProAdmin || isAuthUserOwner === true);

    return {
      canManageUser,
      canEditRole,
      canDeleteUser,
    };
  };

  const handleAddTrigger = () => {
    setIsAddFormVisible(true);
  };

  const handleAddCancel = () => {
    onDraftRoleChange("establishment", null);
    onDraftRoleChange("role", null);
    clearDraftRoleErrors();
    setIsAddFormVisible(false);
  };

  const handleRoleUpdate = (entityId, nextRole, currentRole) => {
    const { canEditRole } = getUserPermissions(entityId, currentRole);

    if (!canEditRole) return;

    onRoleUpdate(entityId, nextRole);
  };

  const handleRoleRemove = (entityId, currentRole) => {
    const { canDeleteUser } = getUserPermissions(entityId, currentRole);

    if (!canDeleteUser) return;

    onRoleRemove(entityId);
  };

  const handleDraftRoleAdd = () => {
    if (!validateDraftRoleForm()) return;

    const establishmentId = draftRole?.establishment?.id;

    if (!establishmentId || !isProAdminForEstablishment(establishmentId)) {
      return;
    }

    onDraftRoleAdd();
  };

  return (
    <div className={styles.container}>
      <div className={styles.userHeader}>
        <div className={styles.avatar}>
          <Text weight="semibold">{userInitial}</Text>
        </div>
        <div className={styles.userMeta}>
          <Text type="label" weight="large">
            {fullName || "-"}
          </Text>
          <Text size="sm" color="subtle">
            {user?.email || "-"}
          </Text>
        </div>
      </div>

      <div className={styles.cardsContainer}>
        {roles.map((role) => {
          const { canEditRole, canDeleteUser } = getUserPermissions(
            role.entityId,
            role.userRole
          );

          return (
            <div key={role.entityId} className={styles.card}>
              <div className={styles.cardInfo}>
                <Text type="label" weight="medium" className={styles.establishmentName}>
                  {role.name}
                </Text>
              </div>
              <div className={styles.cardActions}>
                <div className={styles.rolePicker}>
                  <SelectMenu t={t}
                    name={`existingRole-${role.entityId}`}
                    options={roleOptions}
                    value={role.userRole}
                    disabled={!canEditRole}
                    inputSize="small"
                    onChange={(value, option) =>
                      handleRoleUpdate(role.entityId, option.value, role.userRole)
                    }
                  />
                </div>
                <Button
                  variant="secondary"
                  size="small"
                  label={t("commons.allActions.delete")}
                  disabled={!canDeleteUser}
                  onClick={() => handleRoleRemove(role.entityId, role.userRole)}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.addSection}>
        {!isAddFormVisible ? (
          <Button
            variant="secondary"
            iconName="iconPlus"
            iconPosition="left"
            size="small"
            label={`${t("commons.allActions.add")} ${t(
              "manageUsers.columns.establishment"
            )}`}
            onClick={handleAddTrigger}
          />
        ) : (
          <div className={styles.addForm}>
            <Text type="label" weight="large" size="sm" color="subtle">
              {t("commons.allActions.add")}
            </Text>

            <div className={styles.formRow}>
              <SelectMenu t={t}
                name="draftRoleEstablishment"
                options={establishmentOptions}
                value={draftRole.establishment?.id}
                placeholder={t("manageUsers.columns.establishment")}
                inputSize="small"
                errors={draftRoleErrors.establishment}
                onChange={(value, option) =>
                  onDraftRoleChange("establishment", {
                    id: option.value,
                    name: option.text,
                  })
                }
              />
              <SelectMenu t={t}
                name="draftRoleRole"
                options={roleOptions}
                value={draftRole.role?.value}
                placeholder={t("manageUsers.columns.role")}
                inputSize="small"
                errors={draftRoleErrors.role}
                onChange={(value, option) =>
                  onDraftRoleChange("role", {
                    value: option.value,
                    text: option.text,
                  })
                }
              />
            </div>

            <div className={styles.addFormActions}>
              <Button
                variant="primary"
                label={t("commons.allActions.add")}
                onClick={handleDraftRoleAdd}
              />
              <Button
                variant="secondary"
                label={t("commons.buttons.cancel")}
                onClick={handleAddCancel}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewUser;
